import React, { useState } from "react";
import { format } from "date-fns";
import useTheme from "../../../../common/hooks/useTheme";
import { useAppStore } from "../../../../providers/AppStore";
import {
  getHistoryPrescription,
  updateCustomerPrescription,
} from "../../../../common/network/Api";
import { getApiErrorMessage } from "../../../../common/utils/apiError";
import { getSavedPrescriptionPanels } from "../../utils/labPanelStatus";
import {
  getAnswerSaveErrorSnackbar,
  getPanelQuestions,
} from "../../utils/labPanelQuestions";
import EditLabPanelModal from "../logicBuilder/EditLabPanelModal";
import { Theme } from "../../../../providers/ThemeProvider";
import ScrollableWrapper from "../common/ScrollableWrapper";
import PanelSummaryCard from "../common/PanelSummaryCard";
import InfoSvg from "../../../../common/svgs/InfoSvg";
import LabPanelStatusBanner from "../common/LabPanelStatusBanner";
import {
  resolveLabPanelStatus,
  getStatusBadgeStyle,
  getStatusIconColor,
  labPanelHasResults,
} from "../../utils/labPanelStatus";
import ClientBadge from "../common/ClientBadge";
import PrescriptionLinkButton from "../common/PrescriptionLinkButton";

interface OrderDetailsViewProps {
  order: any;
  /**
   * Show the client badge. Only the cross-client History page needs it — the
   * workspace shells already name the subject in their top bar.
   */
  showClientBadge?: boolean;
  /**
   * Where the "Prescription" link goes — the detail of the prescription this
   * order belongs to. Omitted when the order has no `customerPrescriptionID`.
   */
  prescriptionLink?: string;
  // Called after the order is approved/finalized so the parent can refetch the
  // list (the order's status changed) and return to it.
  onApproved: () => void;
  // Refetch the orders in place (stay on the detail) — used after a failed
  // authorize, since the backend may have mutated the order's status.
  onRefetch?: () => void | Promise<void>;
  // Saving the prescription (to answer the questions) makes the backend
  // re-create its lab panels under new ids, so this order's id goes stale.
  // Called with the re-created panel's id so the route can follow it; without
  // it the view only refetches (and the stale id reads "not found").
  onPanelReplaced?: (newPanelId: string) => void;
}

const OrderDetailsView: React.FC<OrderDetailsViewProps> = ({
  order,
  showClientBadge = false,
  prescriptionLink,
  onApproved,
  onRefetch,
  onPanelReplaced,
}) => {
  const theme: Theme = useTheme();
  const { ASSetOpenSnackBar, ASSetSnackBarMessage, ASSetTypeSnackBar } =
    useAppStore();

  // Answering the lab's order-entry questions from here. The answers belong
  // to the prescription the order was ordered from (CustomerPrescription/
  // Update is the only call that saves them), so the button loads that
  // prescription, finds this order's panel in it — the one object that also
  // carries the start date — and edits it in the same modal the prescription
  // detail uses; saving sends the prescription back with the answered panel.
  const [answerPrescription, setAnswerPrescription] = useState<any | null>(
    null
  );
  const [answerPanel, setAnswerPanel] = useState<any | null>(null);
  // The modal opens on a skeleton the moment the button is clicked and fills
  // in when the prescription arrives.
  const [isLoadingAnswers, setIsLoadingAnswers] = useState(false);

  const findOrderPanel = (prescription: any): any | null => {
    const panels: any[] = Array.isArray(prescription?.labPanels)
      ? prescription.labPanels
      : [];
    // The prescription's panel is this very customer panel (same `id`);
    // the template id is only a fallback for older payloads.
    return (
      panels.find((panel) => String(panel?.id) === String(order?.id)) ??
      panels.find(
        (panel) =>
          panel?.userLabPanelID &&
          String(panel.userLabPanelID) === String(order?.userLabPanelID)
      ) ??
      null
    );
  };

  const handleAnswerQuestions = async () => {
    if (!order?.customerPrescriptionID || isLoadingAnswers) return;
    try {
      setIsLoadingAnswers(true);
      const prescription = await getHistoryPrescription(
        order.customerPrescriptionID
      );
      const panel = prescription ? findOrderPanel(prescription) : null;
      if (!prescription || !panel) {
        setIsLoadingAnswers(false);
        ASSetTypeSnackBar("alert");
        ASSetSnackBarMessage({
          title: "Prescription not found",
          message:
            "The prescription this order belongs to is no longer available, so its questions can't be answered from here.",
        });
        ASSetOpenSnackBar(true);
        return;
      }
      setAnswerPrescription(prescription);
      setAnswerPanel(panel);
    } catch (error: any) {
      ASSetTypeSnackBar("alert");
      ASSetSnackBarMessage({
        title: "Error",
        message: getApiErrorMessage(
          error,
          "Failed to load the prescription. Please try again."
        ),
      });
      ASSetOpenSnackBar(true);
    } finally {
      setIsLoadingAnswers(false);
    }
  };

  const closeAnswerModal = () => {
    setAnswerPanel(null);
    setAnswerPrescription(null);
    setIsLoadingAnswers(false);
  };

  const handleSaveAnswers = async (updatedPanel: any) => {
    try {
      const labPanels = (answerPrescription?.labPanels || []).map(
        (panel: any) =>
          String(panel?.id) === String(updatedPanel?.id) ? updatedPanel : panel
      );
      const saved = await updateCustomerPrescription({
        ...answerPrescription,
        labPanels,
      });
      ASSetTypeSnackBar("success");
      ASSetSnackBarMessage({
        title: "Success",
        message: "Lab panel updated successfully!",
      });
      ASSetOpenSnackBar(true);
      closeAnswerModal();
      // The response carries the re-created panels: this order's is the one
      // from the same panel template. A new id → follow it; otherwise re-read
      // the order in place so it reads "Authorize payment".
      const replaced = getSavedPrescriptionPanels(saved).find(
        (panel) =>
          panel?.userLabPanelID &&
          String(panel.userLabPanelID) === String(updatedPanel?.userLabPanelID)
      );
      const newId = replaced?.id ? String(replaced.id) : null;
      if (newId && newId !== String(order?.id) && onPanelReplaced) {
        onPanelReplaced(newId);
      } else {
        await onRefetch?.();
      }
    } catch (error: any) {
      console.error("Error updating lab panel:", error);
      ASSetTypeSnackBar("alert");
      ASSetSnackBarMessage(
        getAnswerSaveErrorSnackbar(
          error,
          "Failed to update lab panel. Please try again."
        )
      );
      ASSetOpenSnackBar(true);
      // Keep the modal open with the typed answers so they can be corrected.
      throw error;
    }
  };

  // `LabOrderDetailRoute` already renders its not-found state instead of this
  // view, so this is only a type guard — it must not navigate, which is a side
  // effect in render.
  if (!order) return null;

  const customerName = [order.customerFirstName, order.customerLastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  const resolvedStatus = resolveLabPanelStatus(order);
  // Once results are in, the panel card lists each biomarker's result value
  // instead of the plain name list.
  const showResults = labPanelHasResults(order);

  return (
    <div style={styles.container}>
      {/* No title or back button: the breadcrumb names the page and carries
          the link back to the listing (`useBackNavigation`). */}
      <div style={styles.contentWrapper}>
        <ScrollableWrapper>
          <div style={styles.contentContainer(theme)}>
            {/* Panel name + status badge */}
            <div style={styles.panelHeader}>
              <div style={styles.panelHeaderLeft}>
                {/* Status badge beside the title, per the design; the client
                    badge takes the far-right slot. */}
                <div style={styles.panelTitleRow}>
                  <div style={styles.panelName(theme)}>{order.name}</div>
                  {resolvedStatus && (
                    <div style={getStatusBadgeStyle(resolvedStatus.tone, theme)}>
                      {resolvedStatus.actionRequired &&
                        (resolvedStatus.tone === "warning" ||
                          resolvedStatus.tone === "danger") && (
                          <InfoSvg
                            color={getStatusIconColor(resolvedStatus.tone, theme)}
                            size={12}
                          />
                        )}
                      {resolvedStatus.label}
                    </div>
                  )}
                  {prescriptionLink && (
                    <PrescriptionLinkButton to={prescriptionLink} />
                  )}
                </div>
                <div style={styles.subtitle(theme)}>
                  {/* The client is in the badge when it is shown, so naming
                      them here too would just repeat it. */}
                  {!showClientBadge && customerName && (
                    <>
                      {customerName} &bull;{" "}
                    </>
                  )}
                  Ordered{" "}
                  {order.dateCreated
                    ? format(new Date(order.dateCreated), "MMM dd yyyy")
                    : ""}
                </div>
              </div>
              {showClientBadge && (
                <ClientBadge
                  firstName={order.customerFirstName}
                  lastName={order.customerLastName}
                />
              )}
            </div>

            <PanelSummaryCard
              panel={order}
              showResults={showResults}
              showOrderInfo
              beforeBiomarkers={
                <LabPanelStatusBanner
                  panel={order}
                  customerID={order.customerID}
                  onApproved={onApproved}
                  onRefetch={onRefetch}
                  // An order without a prescription (nothing to save the
                  // answers on) shows the message alone.
                  onAnswerQuestions={
                    order.customerPrescriptionID
                      ? handleAnswerQuestions
                      : undefined
                  }
                  marginBottom={0}
                  fontSize={12}
                />
              }
            />
          </div>
        </ScrollableWrapper>
      </div>

      {/* Edit lab panel (answer the order-entry questions) — the same modal
          as the prescription detail, on this order's prescription panel. */}
      <EditLabPanelModal
        open={!!answerPanel || isLoadingAnswers}
        labPanel={answerPanel}
        onClose={closeAnswerModal}
        onSave={handleSaveAnswers}
        type="prescriptionBuilder"
        loading={isLoadingAnswers && !answerPanel}
        loadingQuestionCount={getPanelQuestions(order).length}
      />
    </div>
  );
};

export default OrderDetailsView;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    height: "100%",
  },
  contentWrapper: {
    display: "flex",
    flexDirection: "column" as "column",
    flex: 1,
    minHeight: 0,
    overflow: "hidden" as "hidden",
    marginRight: "-10px",
  },
  contentContainer: (theme: Theme) => ({
    padding: "16px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "24px",
  }),
  panelHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  panelHeaderLeft: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
    minWidth: 0,
  },
  panelTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flexWrap: "wrap" as "wrap",
  },
  panelName: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "20px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  subtitle: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
};
